import { Quote, Clock } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { PageHero } from "../components/layout/PageHero";
import { CtaBand } from "../components/layout/CtaBand";
import { REVIEWS } from "../data/reviews";

const VISIT_REVIEWS = REVIEWS.filter((r) => ["yvonne", "georgea", "jonathan"].includes(r.id));

const TYPES = [
  {
    id: "cats",
    title: "Cat care",
    img: "/images/marypawpins/jingle-black-cat.webp",
    alt: "Jingle the black cat among the garden flowers",
    copy: "Cats stay in their own familiar environment while Carly visits for food, fresh water, clean litter trays, play and company, all according to each cat's individual preferences.",
  },
  {
    id: "puppies",
    title: "Puppy visits",
    img: "/images/marypawpins/springer-puppy-red-bandana.jpg",
    alt: "A springer spaniel puppy in a red bandana",
    copy: "Toileting opportunities, feeding, play and companionship for puppies not yet ready for longer periods alone or a full walking routine, tailored to age, needs and your instructions.",
  },
  {
    id: "seniors",
    title: "Senior dogs",
    img: "/images/marypawpins/grey-lurcher-daisies.webp",
    alt: "An older dog resting among the daisies",
    copy: "A slower pace, a garden visit, companionship or help maintaining an established routine, flexible around mobility and individual comfort.",
  },
  {
    id: "others",
    title: "Other animals",
    img: "/images/marypawpins/ducks-driveway.webp",
    alt: "The ducks coming to say hello",
    copy: "Cats, multi-pet households, and even ducks and chickens have all been part of Mary Pawpins bookings. Other small domestic animals can be considered where Carly has the right experience for their needs.",
  },
];

const INCLUDES = [
  "Feeding and fresh water",
  "Toileting and litter tray cleaning",
  "Play and companionship",
  "Basic routine care",
  "Photo updates",
  "Medication where agreed in advance, with clear instructions",
];

export default function HomeVisitsPage() {
  return (
    <PageShell title="Home Visits">
      <PageHero
        testId="visits-hero"
        eyebrow="Home Visits"
        title="The little things, taken care of."
        copy="Familiar routines and good company. Personal visits for cats, puppies, senior dogs and other small domestic animals while you're out or away."
        image="/images/marypawpins/springer-puppy-red-bandana.jpg"
        alt="A springer spaniel puppy in a red bandana"
        imgPosition="object-[50%_28%]"
        ctaText="Enquire About Home Visits"
        ctaTo="/contact?service=Home%20Visits"
      />

      <section className="py-14 sm:py-20" data-testid="visits-includes">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">What visits can include</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">
              Shaped around the animal.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#575953]">
              Every visit is agreed in advance around your pet's routine. Medication is
              included only where it has been agreed beforehand and is supported by
              clear instructions. Carly will always be honest about what is appropriate.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {INCLUDES.map((item) => (
                <li key={item} className="bg-white rounded-2xl border border-[#DDD7CB] p-5 text-sm text-[#575953]">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#EFECE3]" data-testid="visits-types">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="eyebrow">Who visits are for</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">More than dog walking.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TYPES.map((t) => (
              <article key={t.id} className="bg-white rounded-3xl border border-[#DDD7CB] overflow-hidden" data-testid={`visit-type-${t.id}`}>
                <img src={t.img} alt={t.alt} className="w-full aspect-square object-cover" />
                <div className="p-6">
                  <h3 className="font-serif text-xl font-medium text-[#292A26]">{t.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#575953]">{t.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20" data-testid="visits-pricing">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="rounded-3xl border border-[#DDD7CB] bg-[#E7E2D8]/50 p-7 sm:p-9 flex gap-5 max-w-4xl">
            <Clock className="w-6 h-6 text-[#3F4A38] flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-serif text-2xl font-medium text-[#292A26]">Visit lengths and pricing.</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                Pop-in visits of 15 minutes and longer visits of 30 or 60 minutes are
                available, with multi-visit arrangements for days away. Current prices
                are confirmed when you enquire, so the quote reflects your pet, your
                location and the routine they need.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#EFECE3]" data-testid="visits-reviews">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="eyebrow">Kind words</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3">Settled at home.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {VISIT_REVIEWS.map((r) => (
              <figure key={r.id} className="bg-white rounded-3xl border border-[#DDD7CB] p-6 flex flex-col" data-testid={`visits-review-${r.id}`}>
                <Quote className="w-5 h-5 text-[#D8B7B0]" />
                <blockquote className="mt-4 text-sm leading-relaxed text-[#292A26] flex-1">{r.text}</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-[#E7E2D8]">
                  <p className="text-sm font-semibold text-[#292A26]">{r.attribution}</p>
                  <p className="text-xs text-[#575953]">{r.relation} · {r.source}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        testId="visits-cta"
        heading="Tell me what they need."
        copy="Whether it is a regular visit or care while you're away, Carly can discuss a routine that works for your pet."
        ctaTo="/contact?service=Home%20Visits"
        buttonText="Enquire About Home Visits"
      />
    </PageShell>
  );
}
